/**
 * assembly/input/commands.ts —— 命令清单与派发（§7.2；§7.3.1 frozen）
 * **逐字移植自 102 `frontend_src/src/assembly/input/commands.js`**（含全部注释与判据），JS→TS。
 * 纪律：
 *  - COMMANDS：`Map<string, {handler, mode:'frozen'|'normal', input?}>` —— 命令名唯一事实源；
 *    handler 签名 `(ctx) => { ok, commandId?, error? }`，ctx 由调用方（Composer/父层）注入，本模块不建 ctx。
 *  - **命令提交进 `frozen`**（§7.3.1）：命令行绝不静默降级为普通提示词——
 *    dispatch 未命中 ⇒ 明确报错（返回 {ok:false, error:'unknown_command'}），不转普通文本。
 *  - 带输入的命令（`input:true`）派发为打开输入面板（`leadingInput`），不直接执行（§7.2 三类派发①）。
 *  - 纯数据＋纯调度：无时钟、无 DOM、无网络。
 */

export type CommandMode = "frozen" | "normal";

export interface CommandDef {
  mode: CommandMode;
  input?: boolean;
  describe?: string;
  /** handler 由宿主装配（本表只登记声明） */
  handler?: (ctx: CommandCtx) => Promise<CommandResult> | CommandResult;
}

export type CommandCtx = Record<string, unknown> & {
  openLeadingInput?: (name: string) => void;
};

export interface CommandResult {
  ok: boolean;
  /** 命令的提交语义（frozen/normal）——失败结果同样带回（§7.3.1） */
  mode?: CommandMode;
  commandId?: string;
  error?: string;
}

/** 命令表（含 `/model` 等；§3.9 S1：`/model` 与设置面板读同一份后端计算值，选项由面板/命令同源） */
export const COMMANDS: Map<string, CommandDef> = new Map<string, CommandDef>([
  ["/model", { mode: "frozen", input: true, describe: "切换当前会话模型（打开模型选择输入）" }],
  ["/clear", { mode: "frozen", describe: "清空当前会话" }],
  ["/rename", { mode: "frozen", input: true, describe: "重命名当前会话" }],
  ["/help", { mode: "normal", describe: "显示命令帮助" }],
]);

/** 命令提交语义（§7.3.1）：命令一律 frozen —— 显示「提交中」，不还原草稿，等命令结果落定 */
export const COMMAND_SUBMIT_MODE: CommandMode = "frozen";

/** 未命中命令的显式错误（§7.2 硬规矩：不静默降级） */
export const UNKNOWN_COMMAND = "unknown_command";

/** 未识别命令的**行内提示**文案（U-1c 判据④口径：回车后**作为文本发送** ＋ 给出「未知命令」提示）。
 *  出处＝《前端升级方案_新面补全到旧面退役_20260918》§2 批 U-1 表 U-1c 行判据④原文
 *    「未知命令回车⇒作为文本发送但**给出「未知命令」行内提示**（不静默）」；
 *  与设计件 §7.2 硬规矩（行 1289）「**绝不静默降级**」一致：既走文本路径，也**明确报错**——
 *    区别只在「报错方式」＝行内提示（原文），不再是「只报错不发送」（批 5-A 旧实现）。 */
export function unknownCommandNotice(name?: string | null): string {
  const n = String(name === undefined || name === null ? "" : name);
  return "未知命令 " + n + "（已作为普通消息发送；/help 查看可用命令）";
}

export interface CommandCandidate {
  name: string;
  mode: CommandMode;
  input: boolean;
  describe: string;
}

/** 命令候选（菜单用）：prefix 过滤（§7.1 排序——前缀命中在前，同分保持注册序）。 */
export function commandCandidates(prefix = ""): CommandCandidate[] {
  const p = String(prefix).replace(/^\//, "").toLowerCase();
  const prefixHit: CommandCandidate[] = [];
  const fuzzyHit: CommandCandidate[] = [];
  for (const [name, def] of COMMANDS) {
    const entry: CommandCandidate = { name, mode: def.mode, input: !!def.input, describe: def.describe || "" };
    const bare = name.slice(1).toLowerCase(); // 去掉 sigil 再匹配（调用方给 'm' 或 '/m' 都命中 /model）
    if (!p || bare.startsWith(p)) prefixHit.push(entry);
    else if ([...bare].some((ch) => [...p].includes(ch)) && [...p].every((ch) => bare.includes(ch))) fuzzyHit.push(entry); // 有序子序列模糊命中
  }
  return prefixHit.concat(fuzzyHit); // 完全不命中的不进候选
}

/** 派发命令（§7.2 三类派发的本层职责）。
 *  @returns ok=false 且 error='unknown_command' ⇒ 未注册命令（调用方须显式报错，禁降级为普通提示词）。 */
export async function dispatchCommand(
  name: string,
  ctx: CommandCtx,
  table: Map<string, CommandDef> = COMMANDS,
): Promise<CommandResult> {
  const def = table.get(name);
  if (!def) return { ok: false, error: UNKNOWN_COMMAND };
  // 三类派发①：带 input 的命令 ⇒ 打开其输入面板（由 ctx.openLeadingInput 承接；无则视为失败，不静默）
  if (def.input) {
    if (typeof ctx?.openLeadingInput !== "function") return { ok: false, mode: def.mode, error: "no_input_host" };
    ctx.openLeadingInput(name);
    return { ok: true, mode: def.mode };
  }
  // 其余 ⇒ 直接执行（§7.2 派发③）
  if (typeof def.handler !== "function") {
    // handler 由宿主装配（本表只登记声明）；无 handler ⇒ 显式失败
    return { ok: false, mode: def.mode, error: "no_handler" };
  }
  const r = await def.handler(ctx);
  return r && r.ok ? { ok: true, mode: def.mode, commandId: r.commandId } : { ok: false, mode: def.mode, error: r?.error || "command_failed" };
}

/** 宿主装配：给命令注册 handler（返回新 Map，不改原表——表是声明，行为由宿主注入） */
export function withHandlers(handlers: Record<string, (ctx: CommandCtx) => Promise<CommandResult> | CommandResult>): Map<string, CommandDef> {
  const m = new Map<string, CommandDef>();
  for (const [name, def] of COMMANDS) {
    m.set(name, handlers[name] ? { ...def, handler: handlers[name] } : def);
  }
  return m;
}
